import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

/* ••• Ortak tanımlar ••• */
export const AuthContext = createContext();
export const stillValid = () => !!localStorage.getItem("admin_token");

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

/* 401 yakalayıcıyı yalnızca 1 kez ekle */
const attach401Interceptor = (logout) => {
  if (api.interceptors.response.handlers.length) return; // zaten ekli
  api.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response?.status === 401) logout();
      return Promise.reject(err);
    }
  );
};

/* Profil doğrulaması + timeout */
const verifyProfile = (jwt, t = 7000) => {
  api.defaults.headers.common.Authorization = `Bearer ${jwt}`;
  const req   = api.get("/api/user/profile");            // 200 / 401
  const wait  = new Promise((_, rej) => setTimeout(() => rej(new Error("TIMEOUT")), t));
  return Promise.race([req, wait]);                      // 200  | 401  | TIMEOUT
};

/* ——————————————————————————————————————— */
export function AuthProvider({ children }) {
  const [idleMinutes, setIdleMinutes] = useState(null);  // sunucudan gelir
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [isAuth, setIsAuth] = useState(undefined);       // undefined | true | false
  const idleRef = useRef(null);

  /* 0. idle süresi */
  useEffect(() => {
    api.get("/api/settings")
       .then(r => setIdleMinutes(+r.data.timeout_duration || 30))
       .catch(() => setIdleMinutes(30));
  }, []);

  /* 1. tarayıcı ilk açılış */
  useEffect(() => {
    if (idleMinutes == null) return;

    const jwt = localStorage.getItem("admin_token");
    if (!jwt) { setIsAuth(false); return; }

    /* JWT exp */
    try {
      const { exp } = jwtDecode(jwt);
      if (exp * 1000 < Date.now()) { clearState(); return; }
    } catch { clearState(); return; }

    /* header + interceptor hemen ekle */
    api.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    attach401Interceptor(clearState);

    /* Profil doğrula – sonucu bekleyene kadar isAuth undefined kalır */
    verifyProfile(jwt, 7000)
      .then(() => startSession())
      .catch(err => {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          clearState();              // gerçek yetkisizlik
        } else {
          console.warn("⚠️ Profil doğrulanamadı (timeout) – oturum sürüyor");
          startSession();            // sadece TIMEOUT: oturumu koru
        }
      });
  }, [idleMinutes]);

  /* 2. login */
  const login = (jwt) => {
    localStorage.setItem("admin_token", jwt);
    api.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    attach401Interceptor(clearState);
    startSession();
  };

  /* 3. Ortak: oturumu başlat */
  const startSession = () => {
    setToken(localStorage.getItem("admin_token"));
    setIsAuth(true);
    bumpActivity();
  };

  /* 4. Idle timer */
  const clearIdle = () => clearTimeout(idleRef.current);
  const bumpActivity = () => {
    localStorage.setItem("lastActivity", Date.now().toString());
    clearIdle();
    if (idleMinutes != null)
      idleRef.current = setTimeout(clearState, idleMinutes * 60_000);
  };

  /* 5. Kullanıcı etkileşimleri */
  useEffect(() => {
    if (!token || isAuth !== true) return;
    ["mousemove","click","keydown"].forEach(ev => window.addEventListener(ev, bumpActivity));
    bumpActivity();
    return () => {
      ["mousemove","click","keydown"].forEach(ev => window.removeEventListener(ev, bumpActivity));
      clearIdle();
    };
  }, [token, idleMinutes, isAuth]);

  /* 6. Çıkış + temizleme */
  const clearState = () => {
    clearIdle();
    localStorage.clear();
    delete api.defaults.headers.common.Authorization;
    setToken(null);
    setIsAuth(false);
    window.location.replace("/ctrl/authentication/login/creative");
    setTimeout(() => window.location.reload(), 0);       // bfcache kır
  };

  /* — provider — */
  return (
    <AuthContext.Provider value={{ login, logout: clearState, token, isAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
